import { useState, useEffect } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User as AppUser } from '../types';
import { setAnalyticsUserId, setAnalyticsUserProperties } from '../utils/analytics';

export const useAuth = () => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser({ uid: currentUser.uid, ...userData } as AppUser);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
      if (firebaseUser) {
        try {
          // Get user profile from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const appUser = { uid: firebaseUser.uid, ...userData } as AppUser;
            setUser(appUser);
            
            // Set Analytics user ID and properties
            setAnalyticsUserId(firebaseUser.uid);
            setAnalyticsUserProperties({
              user_type: 'beekeeper',
              registration_date: userData.joined,
              has_apiaries: true // Will be updated when we load their data
            });
          } else {
            // Create new user profile if it doesn't exist
            const newUser: AppUser = {
              uid: firebaseUser.uid,
              name: firebaseUser.displayName || '',
              email: firebaseUser.email || '',
              location: '',
              joined: new Date().toISOString()
            };
            await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
            setUser(newUser);
            
            // Set Analytics for new user
            setAnalyticsUserId(firebaseUser.uid);
            setAnalyticsUserProperties({
              user_type: 'new_beekeeper',
              registration_date: newUser.joined,
              has_apiaries: false
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const register = async (email: string, password: string, name: string, location: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const newUser: AppUser = {
      uid: result.user.uid,
      name,
      email,
      location,
      joined: new Date().toISOString()
    };
    await setDoc(doc(db, 'users', result.user.uid), newUser);
    return result;
  };

  const logout = () => signOut(auth);

  return {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser
  };
};
